import { ScenarioMetadata, ScenarioOrigin } from "in_queue/types/scenarioType";

/**
 * Pick the "canonical base scenario" out of a set of scenarios: the
 * most-recently-created BASE_SCENARIO for which `hasValue` returns true.
 * `hasValue` lets callers narrow to scenarios that have the data they need
 * (e.g. a defined `costPerMw`). Scenarios that never ran are ineligible.
 *
 * Origin — not `type` — identifies a base: NIRA_PUBLISHED scenarios are also
 * type SHARED but are not bases.
 *
 * We sort by `createdAt` because `updatedAt` is bumped by cosmetic edits
 * (rename, archive) and `runCompletedAt` is rewritten whenever
 * `scenario_results` is re-materialized, which would promote an older base.
 *
 * Archived scenarios are eligible, so callers MUST pass the currently-VISIBLE
 * scenario set (e.g. via `filterVisibleScenarios`) to guarantee the chosen
 * base is actually plotted.
 *
 * Returns `undefined` when nothing is eligible; callers can then fall back to
 * a cohort-specific default (e.g. cheapest in view).
 */
export const findLatestBaseScenario = (
    scenarios: ReadonlyArray<ScenarioMetadata>,
    hasValue: (s: ScenarioMetadata) => boolean
): ScenarioMetadata | undefined => {
    return scenarios
        .filter(
            (s) =>
                s.origin === ScenarioOrigin.BaseScenario &&
                s.timeMetadata.runCompletedAt !== undefined &&
                hasValue(s)
        )
        .slice()
        .sort(
            (a, b) =>
                new Date(b.timeMetadata.createdAt).getTime() -
                new Date(a.timeMetadata.createdAt).getTime()
        )[0];
};
